import { useEffect, useMemo, useRef, useState } from "react";
import type { OutputLine, PatchOutputInput } from "@dramapie/shared";

import { outputMetaLabel, sortOutputs } from "../../lib/outputs";

export interface OutputMenuProps {
  open: boolean;
  /** 全量产出线（含 hidden / archived）：管理浮层要能恢复它们。 */
  outputs: readonly OutputLine[];
  currentOutputId: string | null;
  /** 每行「N 集」：`episodes[].outputId` 统计。 */
  episodeCounts: Record<string, number>;
  /** 触发按钮（Rail 里那一行）：浮层按它的位置贴边展开。 */
  anchor: HTMLElement | null;
  busy?: boolean;
  onClose: () => void;
  onSelect: (outputId: string) => void;
  onCreate: () => void;
  onRename: (output: OutputLine) => void;
  onPatch: (output: OutputLine, patch: PatchOutputInput) => void;
  onDelete: (output: OutputLine) => void;
}

interface MenuPosition {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
}

/**
 * 产出线浮层（契约 M8 §4 验收 1/2）。
 *
 * 按原型 `renderOutputMenu` 的布局：
 *   · 顶部一行「产出线 · N」+ 右上「＋」；
 *   · 全部产出线一行一条，含隐藏 / 归档的（置灰 + 状态标签）；
 *   · 每行右侧「⋯」展开二级菜单：重命名 / 隐藏 / 归档 / 删除，隐藏或归档的给「恢复」。
 *
 * 为什么用 `position: fixed` 而不是在左栏里 `absolute`：
 * 左栏是 `overflow: auto`，浮层比栏宽（原型 280px+），absolute 会被裁掉；
 * fixed 相对视口定位，不被祖先的 overflow 裁剪。
 */
export function OutputMenu({
  open,
  outputs,
  currentOutputId,
  episodeCounts,
  anchor,
  busy = false,
  onClose,
  onSelect,
  onCreate,
  onRename,
  onPatch,
  onDelete,
}: OutputMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const [subId, setSubId] = useState<string | null>(null);

  const items = useMemo(() => sortOutputs(outputs), [outputs]);

  // 浮层贴住左栏那一行：展开位置随窗口 / 左栏滚动变化，重新量一次。
  useEffect(() => {
    if (!open || !anchor) {
      setPosition(null);
      return;
    }
    const measure = () => {
      const rect = anchor.getBoundingClientRect();
      const width = Math.max(rect.width, 300);
      const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - width - 8));
      const top = rect.bottom + 6;
      setPosition({
        left,
        top,
        width,
        maxHeight: Math.max(160, window.innerHeight - top - 16),
      });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, anchor]);

  // 打开时把焦点放进浮层；Esc 关闭并把焦点还给触发按钮。
  useEffect(() => {
    if (!open) {
      setSubId(null);
      return;
    }
    const timer = setTimeout(() => {
      menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }, 0);

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
      anchor?.focus();
    }
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      // 触发按钮自己负责 toggle，别在这里抢。
      if (anchor?.contains(target)) return;
      onClose();
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, onClose, anchor]);

  if (!open) return null;

  const style = position
    ? { left: position.left, top: position.top, width: position.width, maxHeight: position.maxHeight }
    : undefined;

  return (
    <div
      ref={menuRef}
      className="dp-om"
      role="menu"
      aria-label="产出线"
      style={style}
      data-testid="output-menu"
    >
      <div className="dp-om-head">
        <span>产出线 · {items.length}</span>
        <button
          type="button"
          className="dp-om-add"
          aria-label="新建产出线"
          title="新建产出线"
          disabled={busy}
          onClick={onCreate}
        >
          ＋
        </button>
      </div>

      <div className="dp-om-list">
        {items.length === 0 ? <p className="dp-om-empty">还没有产出线</p> : null}
        {items.map((output) => {
          const muted = output.hidden || output.archived;
          const active = output.id === currentOutputId;
          const count = episodeCounts[output.id] ?? 0;
          return (
            <div
              key={output.id}
              className={`dp-om-item${active ? " is-active" : ""}${muted ? " is-muted" : ""}`}
            >
              <button
                type="button"
                role="menuitem"
                className="dp-om-select"
                aria-current={active ? "true" : undefined}
                onClick={() => onSelect(output.id)}
              >
                <span className="dp-om-mark" aria-hidden="true">
                  {output.title.slice(0, 1)}
                </span>
                <span className="dp-om-copy">
                  <span className="dp-om-name">
                    {output.title}
                    {output.archived ? <i>已归档</i> : null}
                    {output.hidden && !output.archived ? <i>已隐藏</i> : null}
                  </span>
                  <span className="dp-om-meta">{outputMetaLabel(output, count)}</span>
                </span>
              </button>
              <button
                type="button"
                className="dp-om-more"
                aria-label={`管理「${output.title}」`}
                aria-haspopup="menu"
                aria-expanded={subId === output.id}
                title="管理产出线"
                onClick={() => setSubId((prev) => (prev === output.id ? null : output.id))}
              >
                ⋯
              </button>

              {subId === output.id ? (
                <div className="dp-om-sub" role="menu" aria-label={`管理「${output.title}」`}>
                  <button
                    type="button"
                    role="menuitem"
                    disabled={busy || output.archived}
                    title={
                      output.archived
                        ? "已归档（只读）：先恢复再改名 / 改形态"
                        : "重命名 / 改形态 / 改画幅"
                    }
                    onClick={() => {
                      setSubId(null);
                      onRename(output);
                    }}
                  >
                    重命名
                  </button>
                  {muted ? (
                    <button
                      type="button"
                      role="menuitem"
                      disabled={busy}
                      onClick={() => {
                        setSubId(null);
                        onPatch(output, { hidden: false, archived: false });
                      }}
                    >
                      恢复
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={busy}
                        onClick={() => {
                          setSubId(null);
                          onPatch(output, { hidden: true });
                        }}
                      >
                        隐藏
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={busy}
                        onClick={() => {
                          setSubId(null);
                          onPatch(output, { archived: true });
                        }}
                      >
                        归档
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    className="is-danger"
                    disabled={busy}
                    onClick={() => {
                      setSubId(null);
                      onDelete(output);
                    }}
                  >
                    删除
                  </button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="dp-om-sep" aria-hidden="true" />
      <button type="button" className="dp-om-action" disabled={busy} onClick={onCreate}>
        ＋ 新建产出线
      </button>
    </div>
  );
}
